export const BUSINESS_NETWORK_EVENT_SERIES = "urban-unity-business-network";
export const BUSINESS_NETWORK_SEPTEMBER_EVENT_ID = "business-network-2026-09-30";

const SEPTEMBER_EVENT_ALIASES = new Set([
  "urban-unity-business-network-launch-2026",
  "urban-unity-business-network-launch",
  BUSINESS_NETWORK_SEPTEMBER_EVENT_ID,
]);

const MONTH_SLUGS = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december",
];

export const LEGACY_SEPTEMBER_BUSINESS_NETWORK_EVENT = Object.freeze({
  id: BUSINESS_NETWORK_SEPTEMBER_EVENT_ID,
  sourceId: "urban-unity-business-network-launch-2026",
  eventId: BUSINESS_NETWORK_SEPTEMBER_EVENT_ID,
  eventSeries: BUSINESS_NETWORK_EVENT_SERIES,
  eventType: "business-network",
  eventName: "Urban Unity Business Network Launch",
  title: "Urban Unity Business Network Launch",
  slug: "urban-unity-business-network-launch-2026",
  eventSlug: "urban-unity-business-network-launch-2026",
  date: "2026-09-30",
  startTime: "17:30",
  endTime: "19:00",
  venueName: "Phoenix Heights Community Centre",
  addressLine1: "140a Byng Street",
  area: "Isle of Dogs",
  city: "London",
  postcode: "E14 9AR",
  description: "The launch of the Urban Unity Business Network.",
  status: "completed",
  registrationOpen: false,
  timezone: "Europe/London",
  historical: true,
});

function clean(value) {
  return String(value ?? "").normalize("NFKC").trim();
}

function asMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function validDateKey(value) {
  const dateKey = clean(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return "";
  const parsed = new Date(`${dateKey}T12:00:00.000Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === dateKey
    ? dateKey
    : "";
}

function londonDateKey(value) {
  const milliseconds = asMillis(value);
  if (!milliseconds) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(milliseconds));
  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${byType.year}-${byType.month}-${byType.day}`;
}

function eventDateFromId(value) {
  const match = clean(value).match(/(\d{4}-\d{2}-\d{2})/);
  return match ? validDateKey(match[1]) : "";
}

function eventDateMillis(event, row) {
  const dateKey = event?.date || eventDateFromId(row.eventId);
  if (dateKey) return new Date(`${dateKey}T12:00:00.000Z`).getTime();
  return row.createdAtMillis || 0;
}

function splitLegacyName(value) {
  const parts = clean(value).split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
}

export function canonicalBusinessNetworkEventId(value) {
  const eventId = clean(value);
  if (!eventId || SEPTEMBER_EVENT_ALIASES.has(eventId)) {
    return BUSINESS_NETWORK_SEPTEMBER_EVENT_ID;
  }
  return eventId;
}

export function buildBusinessNetworkEventIdentity(dateValue) {
  const date = validDateKey(dateValue);
  if (!date) return { eventId: "", eventSlug: "" };
  const [year, month] = date.split("-").map(Number);
  return {
    eventId: `business-network-${date}`,
    eventSlug: `urban-unity-business-network-${MONTH_SLUGS[month - 1]}-${year}`,
  };
}

export function businessNetworkEventStartDate(dateValue, timeValue) {
  const date = validDateKey(dateValue);
  const time = clean(timeValue);
  const match = time.match(/^(\d{2}):(\d{2})$/);
  if (!date || !match) return null;
  const [year, month, day] = date.split("-").map(Number);
  const targetUtc = Date.UTC(year, month - 1, day, Number(match[1]), Number(match[2]));
  let candidate = targetUtc;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date(candidate));
    const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    const renderedAsUtc = Date.UTC(
      Number(byType.year),
      Number(byType.month) - 1,
      Number(byType.day),
      Number(byType.hour),
      Number(byType.minute),
    );
    candidate -= renderedAsUtc - targetUtc;
  }
  return new Date(candidate);
}

export function validateBusinessNetworkEventDraft(values = {}) {
  const errors = {};
  const date = validDateKey(values.date);
  const startTime = clean(values.startTime);
  const endTime = clean(values.endTime);
  const capacity = clean(values.capacity);

  if (!clean(values.eventName) || clean(values.eventName).length > 160) {
    errors.eventName = "Enter an event title of up to 160 characters.";
  }
  if (!date) errors.date = "Enter a valid event date.";
  if (!/^\d{2}:\d{2}$/.test(startTime)) errors.startTime = "Enter a start time.";
  if (!/^\d{2}:\d{2}$/.test(endTime)) errors.endTime = "Enter an end time.";
  if (!errors.startTime && !errors.endTime && endTime <= startTime) {
    errors.endTime = "The end time must be later than the start time.";
  }
  if (!clean(values.venueName)) errors.venueName = "Enter a venue name.";
  if (!clean(values.addressLine1)) errors.addressLine1 = "Enter the first address line.";
  if (!clean(values.city)) errors.city = "Enter a city.";
  if (!clean(values.postcode)) errors.postcode = "Enter a postcode.";
  if (!clean(values.description) || clean(values.description).length > 3000) {
    errors.description = "Enter a description of up to 3,000 characters.";
  }
  if (capacity && (!/^\d+$/.test(capacity) || Number(capacity) < 1 || Number(capacity) > 10000)) {
    errors.capacity = "Capacity must be a whole number between 1 and 10,000.";
  }
  return errors;
}

export function buildBusinessNetworkEventRecord(values = {}) {
  const errors = validateBusinessNetworkEventDraft(values);
  if (Object.keys(errors).length) {
    const error = new Error("BUSINESS_NETWORK_EVENT_INVALID");
    error.validationErrors = errors;
    throw error;
  }
  const identity = buildBusinessNetworkEventIdentity(values.date);
  const address = [values.addressLine1, values.area, values.city, values.postcode]
    .map(clean)
    .filter(Boolean)
    .join(", ");
  const capacityText = clean(values.capacity);
  return {
    ...identity,
    slug: identity.eventSlug,
    eventSeries: BUSINESS_NETWORK_EVENT_SERIES,
    eventName: clean(values.eventName),
    title: clean(values.eventName),
    eventType: "business-network",
    type: "event",
    date: validDateKey(values.date),
    startDate: validDateKey(values.date),
    startTime: clean(values.startTime),
    endTime: clean(values.endTime),
    time: clean(values.startTime),
    venueName: clean(values.venueName),
    addressLine1: clean(values.addressLine1),
    area: clean(values.area),
    city: clean(values.city),
    postcode: clean(values.postcode).toUpperCase(),
    location: clean(values.venueName),
    address,
    description: clean(values.description),
    status: "approved",
    lifecycleStatus: "upcoming",
    registrationOpen: values.registrationOpen === true,
    capacity: capacityText ? Number(capacityText) : null,
    timezone: "Europe/London",
    isFree: true,
    cost: "Free",
  };
}

export function normaliseBusinessNetworkEvent(id, raw = {}) {
  const sourceId = clean(raw.eventId || id);
  const eventId = canonicalBusinessNetworkEventId(sourceId);
  const date = validDateKey(raw.date) || londonDateKey(raw.eventDate) || eventDateFromId(eventId);
  const identity = buildBusinessNetworkEventIdentity(date);
  return {
    ...raw,
    id: eventId,
    sourceId: sourceId || id,
    eventId,
    eventSeries: raw.eventSeries || BUSINESS_NETWORK_EVENT_SERIES,
    eventType: raw.eventType || (raw.type === "business-network" ? raw.type : "business-network"),
    eventName: raw.eventName || raw.title || (eventId === BUSINESS_NETWORK_SEPTEMBER_EVENT_ID
      ? LEGACY_SEPTEMBER_BUSINESS_NETWORK_EVENT.eventName
      : "Urban Unity Business Network event"),
    title: raw.title || raw.eventName || "Urban Unity Business Network event",
    slug: raw.slug || raw.eventSlug || identity.eventSlug,
    eventSlug: raw.eventSlug || raw.slug || identity.eventSlug,
    date,
    startTime: raw.startTime || "",
    endTime: raw.endTime || "",
    venueName: raw.venueName || "",
    addressLine1: raw.addressLine1 || "",
    area: raw.area || "",
    city: raw.city || "",
    postcode: raw.postcode || "",
    status: raw.status || "approved",
    registrationOpen: raw.registrationOpen === true,
    timezone: raw.timezone || "Europe/London",
    eventDateMillis: date ? new Date(`${date}T12:00:00.000Z`).getTime() : asMillis(raw.eventDate),
  };
}

export function normaliseBusinessNetworkRegistration(id, raw = {}) {
  const legacyName = splitLegacyName(raw.contactName);
  const firstName = raw.firstName || legacyName.firstName;
  const lastName = raw.lastName || legacyName.lastName;
  const createdAt = raw.createdAt || raw.submittedAt || null;
  const email = raw.email || raw.contactEmail || "";
  const rawPreviousAttendance = raw.previousBusinessNetworkAttendee;
  const previousBusinessNetworkAttendee = rawPreviousAttendance === true
    || clean(rawPreviousAttendance).toLowerCase() === "yes";
  return {
    ...raw,
    id,
    registrationId: raw.registrationId || id,
    eventId: canonicalBusinessNetworkEventId(raw.eventId || raw.eventKey),
    eventName: raw.eventName || "Urban Unity Business Network Launch Event",
    firstName,
    lastName,
    fullName: raw.fullName || [firstName, lastName].filter(Boolean).join(" ") || raw.contactName || "—",
    email,
    emailNormalized: clean(raw.emailNormalized || email).toLowerCase(),
    businessOrganisation: raw.businessOrganisation || raw.companyName || "",
    roleOrJobTitle: raw.roleOrJobTitle || "",
    phone: raw.phone || raw.contactPhone || "",
    registrationStatus: raw.registrationStatus || (raw.status === "cancelled" ? "cancelled" : "confirmed"),
    attendanceStatus: raw.attendanceStatus || "registered",
    registrationSource: raw.registrationSource || raw.source || "website",
    previousBusinessNetworkAttendee,
    previousAttendanceAnswerKnown: typeof rawPreviousAttendance === "boolean"
      || ["yes", "no"].includes(clean(rawPreviousAttendance).toLowerCase()),
    marketingConsent: raw.marketingConsent === true,
    marketingConsentKnown: Number(raw.registrationVersion) >= 2,
    eventConsent: raw.eventConsent === true || raw.contactConsent === true,
    registrationVersion: Number(raw.registrationVersion) || 1,
    createdAt,
    createdAtMillis: asMillis(createdAt),
  };
}

export function mergeBusinessNetworkEvents(events = [], registrations = []) {
  const merged = new Map();
  const september = { ...LEGACY_SEPTEMBER_BUSINESS_NETWORK_EVENT };
  september.eventDateMillis = new Date(`${september.date}T12:00:00.000Z`).getTime();
  merged.set(september.eventId, september);

  events.forEach((event) => {
    const normalised = normaliseBusinessNetworkEvent(event.sourceId || event.id, event);
    if (
      normalised.eventSeries === BUSINESS_NETWORK_EVENT_SERIES
      || normalised.eventType === "business-network"
      || normalised.eventId === BUSINESS_NETWORK_SEPTEMBER_EVENT_ID
    ) {
      const existing = merged.get(normalised.eventId) || {};
      merged.set(normalised.eventId, { ...existing, ...normalised });
    }
  });

  registrations.forEach((row) => {
    const eventId = canonicalBusinessNetworkEventId(row.eventId || row.eventKey);
    if (merged.has(eventId)) return;
    const date = eventDateFromId(eventId);
    merged.set(eventId, normaliseBusinessNetworkEvent(eventId, {
      eventId,
      eventName: row.eventName || "Urban Unity Business Network event",
      date,
      status: date && date < londonDateKey(new Date()) ? "completed" : "approved",
      registrationOpen: false,
    }));
  });

  return [...merged.values()].sort((first, second) => (
    (second.eventDateMillis || 0) - (first.eventDateMillis || 0)
      || first.eventName.localeCompare(second.eventName)
  ));
}

export function selectDefaultBusinessNetworkEvent(events, now = new Date()) {
  const today = londonDateKey(now);
  const upcoming = events
    .filter((event) => event.date && event.date >= today && event.status === "approved")
    .sort((first, second) => first.date.localeCompare(second.date));
  return upcoming[0]?.eventId || events[0]?.eventId || BUSINESS_NETWORK_SEPTEMBER_EVENT_ID;
}

export function classifyBusinessNetworkRegistrations(rows, events = []) {
  const eventMap = new Map(events.map((event) => [event.eventId, event]));
  const groups = new Map();
  rows.forEach((row, index) => {
    const key = row.emailNormalized || `missing-email:${row.id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ row, index });
  });

  const result = new Array(rows.length);
  groups.forEach((members) => {
    const sorted = [...members].sort((first, second) => {
      const firstDate = eventDateMillis(eventMap.get(first.row.eventId), first.row);
      const secondDate = eventDateMillis(eventMap.get(second.row.eventId), second.row);
      return firstDate - secondDate
        || first.row.createdAtMillis - second.row.createdAtMillis
        || first.row.id.localeCompare(second.row.id);
    });
    const firstEventId = sorted[0]?.row.eventId;
    sorted.forEach(({ row, index }) => {
      const automaticReturning = Boolean(row.emailNormalized && row.eventId !== firstEventId);
      const isReturningAttendee = automaticReturning || row.previousBusinessNetworkAttendee === true;
      result[index] = {
        ...row,
        isReturningAttendee,
        attendeeType: isReturningAttendee ? "returning" : "first",
        attendeeTypeSource: automaticReturning ? "email-history" : row.previousBusinessNetworkAttendee
          ? "self-reported"
          : "first-recorded-event",
      };
    });
  });
  return result;
}

export function filterBusinessNetworkRegistrations(rows, filters) {
  const needle = clean(filters.search).toLowerCase();
  const filtered = rows.filter((row) => {
    const searchable = [
      row.fullName,
      row.email,
      row.businessOrganisation,
      row.roleOrJobTitle,
      row.phone,
      row.attendeeNames,
    ].join(" ").toLowerCase();
    return (!needle || searchable.includes(needle))
      && (!filters.registrationStatus || row.registrationStatus === filters.registrationStatus)
      && (!filters.attendanceStatus || row.attendanceStatus === filters.attendanceStatus)
      && (!filters.source || row.registrationSource === filters.source)
      && (!filters.attendeeType || row.attendeeType === filters.attendeeType);
  });
  return filtered.sort((first, second) => {
    if (filters.sort === "oldest") return first.createdAtMillis - second.createdAtMillis;
    if (filters.sort === "name") return first.fullName.localeCompare(second.fullName);
    if (filters.sort === "business") return first.businessOrganisation.localeCompare(second.businessOrganisation);
    return second.createdAtMillis - first.createdAtMillis;
  });
}

export function businessNetworkMetrics(rows, events = []) {
  const active = rows.filter((row) => row.registrationStatus !== "cancelled");
  return {
    totalEvents: events.length,
    totalRegistrations: rows.length,
    registered: active.length,
    attended: active.filter((row) => row.attendanceStatus === "attended").length,
    noShow: active.filter((row) => row.attendanceStatus === "no_show").length,
    cancelled: rows.filter((row) => row.registrationStatus === "cancelled").length,
    uniqueAttendees: new Set(active.map((row) => row.emailNormalized).filter(Boolean)).size,
    firstTime: active.filter((row) => row.attendeeType === "first").length,
    returning: active.filter((row) => row.attendeeType === "returning").length,
    marketingOptIns: active.filter((row) => row.marketingConsent).length,
  };
}

export function formatBusinessNetworkDate(value) {
  const milliseconds = asMillis(value);
  if (!milliseconds) return "Date unavailable";
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Europe/London",
  }).format(new Date(milliseconds));
}

export function formatBusinessNetworkEventDate(event) {
  const date = validDateKey(event?.date);
  if (!date) return "Date to be confirmed";
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Europe/London",
  }).format(new Date(`${date}T12:00:00.000Z`));
}

function csvSafe(value) {
  const text = String(value ?? "");
  const protectedText = /^\s*[=+\-@]/.test(text) ? `'${text}` : text;
  return `"${protectedText.replace(/"/g, '""')}"`;
}

const CSV_COLUMNS = [
  ["Registration ID", "registrationId"],
  ["First name", "firstName"],
  ["Last name", "lastName"],
  ["Email", "email"],
  ["Business or organisation", "businessOrganisation"],
  ["Role or job title", "roleOrJobTitle"],
  ["Phone", "phone"],
  ["Event", "eventName"],
  ["Event ID", "eventId"],
  ["Registration date", "createdAt"],
  ["Registration source", "registrationSource"],
  ["First or returning", "attendeeType"],
  ["Registration status", "registrationStatus"],
  ["Attendance status", "attendanceStatus"],
  ["Event consent", "eventConsent"],
  ["Marketing consent", "marketingConsent"],
  ["Legacy attendee count", "attendeeCount"],
  ["Legacy attendee names", "attendeeNames"],
];

export function buildBusinessNetworkCsv(rows, event = null) {
  const header = CSV_COLUMNS.map(([label]) => csvSafe(label)).join(",");
  const body = rows.map((row) => CSV_COLUMNS.map(([, key]) => {
    if (key === "createdAt") return csvSafe(formatBusinessNetworkDate(row.createdAt));
    if (key === "eventName") return csvSafe(event?.eventName || row.eventName);
    if (key === "marketingConsent") {
      return csvSafe(row.marketingConsentKnown === false ? "Not collected (legacy)" : row.marketingConsent ? "Yes" : "No");
    }
    if (key === "eventConsent") return csvSafe(row.eventConsent ? "Yes" : "No");
    if (key === "attendeeType") return csvSafe(row.attendeeType === "returning" ? "Returning" : "First-time");
    return csvSafe(row[key]);
  }).join(","));
  return `\uFEFF${[header, ...body].join("\r\n")}`;
}

export function businessNetworkCsvFilename(event) {
  const date = validDateKey(event?.date) || eventDateFromId(event?.eventId) || "undated";
  return `urban-unity-business-network-${date}-registrations.csv`;
}
